import { DependencyList, useEffect, useState } from 'react';
import SnackbarStore from '../../stores/observables/SnackbarStore';

type Resource<Schema> = [
  Schema, // loaded resource
  boolean, // is resource ready
  boolean, // an error occurred
  React.Dispatch<React.SetStateAction<Schema>> // resource setter
];

/**
 * Simple hook for performing the async logic to load a resource, without having
 * to deal with the useEffect async/await and flag management code. It supports
 * a dependency array for controlling when the data must be refetched and a
 * conditional flag to load the resources, which is added to the dependencies
 * as well.
 *
 * Usage:
 *   const ownerId = new URLSearchParams(location.search).get('owner');
 *
 *   const [myResources, myResourcesReady, error, setMyResources] = useResource<MyResource[]>(
 *     () => loadMyResources(), // function that performs the data fetch and returns a promise
 *     [], // default value; use useResource<T | undefined> to allow undefined default value
 *     [ownerId], // dependency list; the resource will be loaded when this changes
 *     ownerId !== undefined, // [optional] conditional flag if the data should be loaded
 *   );
 *
 *   // a more simple approach
 *   const [foo, fooReady] = useResource<Foo | undefined>(
 *     () => loadFoo(), undefined, []
 *   );
 *
 *   // an even simpler approach (type inferred by TS)
 *   const [foo, fooReady] = useResource(
 *     () => new FooInterface().search(), undefined, []
 *   );
 *
 *   const loadMyResources = (): Promise<MyResource[]> => {
 *     return fetch('https://backend/my-resource', { ownerId });
 *   };
 *
 *   // use `setResource` to update the resources without having to perform
 *   // another request. You could use this to add, update or deleting resources.
 *   const onMyResourceCreated = (myResource: MyResource) => {
 *     setMyResources(prev => [...prev, myResource]);
 *   };
 */
function useResource<Schema>(
  loadFn: () => Promise<Schema>,
  defaultValue: Schema,
  dependencies: DependencyList,
  isReadyToLoad?: boolean,
  errorHandler?: (error: any) => void
): Resource<Schema> {
  const [error, setError] = useState(false);
  const [ready, setReady] = useState(false);
  const [resource, setResource] = useState<Schema>(defaultValue);

  useEffect(() => {
    let active = true;

    (async () => {
      try {
        if (isReadyToLoad === false) {
          return;
        }

        setReady(false);

        const fetchedResource = await loadFn();

        if (!active) {
          return;
        }

        setResource(fetchedResource);
        setReady(true);
      } catch (e) {
        setError(true);

        if (errorHandler) {
          return errorHandler(e);
        }

        if (e) SnackbarStore.show(`${e}`);
      }
    })();

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...dependencies, isReadyToLoad]);

  return [resource, ready, error, setResource];
}

export default useResource;
